<template>
  <div>
    <v-container fluid class="pa-2">
      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-4">
        <div>
          <h2 class="section-title">
          {{$t('ProtocolManagement.ProtocolManagementTitle')}}
          </h2>
          <div class="text-h6 font-weight-bold"></div>
          <div class="text-caption grey--text">
          </div>
        </div>

        <div class="d-flex align-center" style="gap: 12px;">
          <v-btn color="primary" @click="openCreateProtocol">
            <v-icon left>mdi-plus</v-icon>
            New Protocol
          </v-btn>

          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-magnify"
            placeholder="Search"
            style="width: 320px;"
          />
        </div>
      </div>

      <!-- Card -->
      <v-card class="table-card" outlined>
        <v-data-table
          :headers="getheaders"
          :items="filteredItems"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          class="elevation-1"
          hide-default-footer
        >
          <!-- # -->
          <template v-slot:item.no="{ index }">
            {{ (page - 1) * itemsPerPage + index + 1 }}
          </template>

          <!-- Actions (อยู่แถวเดียวกัน) -->
          <template v-slot:item.actions="{ item }">
            <div class="action-inline">
              <v-btn icon small text elevation="0" color="primary" @click="openEditProtocol(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>

              <v-btn icon small text elevation="0" color="red" @click="confirmDeleteProtocol(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Transport Type เป็น chip -->
          <template v-slot:item.transportType="{ item }">
            <v-chip
              small
              class="mr-1"
              :color="transportColor(item.transportType)"
              text-color="white"
            >
              {{ item.transportType || '-' }}
            </v-chip>
          </template>

          <!-- Description -->
          <template v-slot:item.description="{ item }">
            <span class="grey--text text--darken-2">
              {{ item.description || "-" }}
            </span>
          </template>

          <!-- Dates -->
          <template v-slot:item.dateCreated="{ item }">
            <span class="grey--text text--darken-2">{{ item.dateCreated || "-" }}</span>
          </template>

          <template v-slot:item.dateChanged="{ item }">
            <span class="grey--text text--darken-2">{{ item.dateChanged || "-" }}</span>
          </template>

          <template v-slot:no-data>
            <div class="pa-6 text-center grey--text">No protocols found</div>
          </template>
        </v-data-table>

        <!-- Footer / Pagination -->
        <div class="d-flex align-center justify-space-between px-4 py-3 footer-bar">
          <div class="text-caption grey--text">
            Showing {{ showingFrom }}-{{ showingTo }} of {{ filteredItems.length }} protocols
          </div>

          <div class="d-flex align-center" style="gap: 10px;">
            <v-btn icon small text elevation="0" :disabled="page <= 1" @click="page--">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <div class="text-caption grey--text text--darken-2">
              Page {{ page }} of {{ totalPages }}
            </div>

            <v-btn icon small text elevation="0" :disabled="page >= totalPages" @click="page++">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-container>

    <!-- Create / Edit Protocol Dialog -->
    <v-dialog v-model="dialog.open" max-width="720" persistent >
      <v-card class="dialog-card">
        <v-btn
          icon
          class="dialog-close-btn"
          @click="closeDialog"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <!-- Title -->
        <v-card-title class="pb-0">
          <span class="text-h4 font-weight-bold">
            {{ dialog.mode === 'create' ? $t('ProtocolManagement.buttonNewProtocol') : $t('ProtocolManagement.buttonEditProtocol') }}
          </span>
        </v-card-title>

        <v-divider class="mt-1" />

          <!-- Content -->
          <v-card-text>
            <v-form ref="protocolForm" v-model="dialog.valid" lazy-validation>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.protocolCode"
                    :label="this.$t('ProtocolManagement.protocolCode')"
                    outlined dense
                    :rules="[rules.required]"
                    :disabled="dialog.mode === 'edit'"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.protocolName"
                    :label="this.$t('ProtocolManagement.protocolName')"
                    outlined dense
                    :rules="[rules.required]"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.transportType"
                    :items="transportOptions"
                    label="Transport Type"
                    outlined dense
                    :rules="[rules.required]"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.description"
                    :label="this.$t('ProtocolManagement.description')"
                    outlined dense
                    rows="2"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-divider />

          <!-- Actions -->
          <v-card-actions class="dialog-actions">
            <v-spacer />
            <v-btn text @click="closeDialog">
              {{$t('cancel')}}
            </v-btn>
            <v-btn
              color="primary"
              :loading="dialog.loading"
              @click="saveProtocol"
            >
              {{$t('save')}}
            </v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.dialogProgress" persistent width="300" eager>
      <v-card color="primary" dark>
        <v-card-text>
          {{progressMessage}}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>
<script>
export default {
  name: 'ProtocolManagement',

  data () {
    return {
      search: '',
      page: 1,
      itemsPerPage: 20,
      progressMessage: '',
      items: [],

      dialog: {
        open: false,
        mode: 'create', // create | edit
        valid: true,
        loading: false
      },
      form: {
        protocolSkey: '',
        protocolCode: '',
        protocolName: '',
        transportType: '',
        description: ''
      },
      rules: {
        required: v => !!v || 'Required'
      },
      transportOptions: [
        'TCP',
        'HTTP',
        'SERIAL',
        'FILE',
        'WS'
      ]
    }
  },

  created () {
    // ✅ Vue2: เรียกตอน component ถูกสร้าง
    this.fetchItems()
  },

  computed: {
    getheaders () {
      return [
        { text: '#', value: 'no', width: 40 },
        { text: 'Actions', value: 'actions', sortable: false, width: 120, align: 'center' },
        // { text: 'Protocol SKey', value: 'protocolSkey' },
        { text: this.$t('ProtocolManagement.protocolCode'), value: 'protocolCode' },
        { text: this.$t('ProtocolManagement.protocolName'), value: 'protocolName' },
        { text: this.$t('ProtocolManagement.transportType'), value: 'transportType' },
        { text: this.$t('ProtocolManagement.description'), value: 'description' },
        { text: 'Date Created', value: 'dateCreated' },
        { text: 'User Created', value: 'userCreated' },
        { text: 'Date Changed', value: 'dateChanged' },
        { text: 'User Changed', value: 'userChanged' }
      ]
    },
    filteredItems () {
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return this.items

      return this.items.filter(x => {
        return (
          String(x.protocolCode || '').toLowerCase().includes(q) ||
          String(x.protocolName || '').toLowerCase().includes(q) ||
          String(x.transportType || '').toLowerCase().includes(q)
        )
      })
    },

    totalPages () {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage))
    },
    showingFrom () {
      if (this.filteredItems.length === 0) return 0
      return (this.page - 1) * this.itemsPerPage + 1
    },
    showingTo () {
      return Math.min(this.page * this.itemsPerPage, this.filteredItems.length)
    }
  },

  watch: {
    search () { this.page = 1 },
    filteredItems () { if (this.page > this.totalPages) this.page = this.totalPages }
  },

  methods: {
    closeDialog () {
      this.dialog.open = false
      if (this.$refs.clientForm) {
        this.$refs.clientForm.resetValidation()
      }
    },
    transportColor (t) {
      const v = String(t || '').toUpperCase()
      if (v === 'HTTP') return 'blue'
      if (v === 'TCP') return 'indigo'
      if (v === 'SERIAL') return 'teal'
      if (v === 'WS' || v === 'WEBSOCKET') return 'deep-purple'
      if (v === 'FILE') return 'grey'
      return 'primary'
    },
    async fetchItems () {
      this.listLoading = true
      this.listError = ''

      try {
        const endpoint = `${process.env.VUE_APP_API}/api/${process.env.VUE_APP_API_VERSION}/protocolManagement`
        const res = await this.axios.get(endpoint)

        const rows = Array.isArray(res.data?.data)
          ? res.data.data
          : []

        this.items = rows.map(x => ({
          protocolSkey: x.protocol_skey || x.protocolSkey || null,
          protocolCode: x.protocol_code || x.protocolCode || '',
          protocolName: x.protocol_name || x.protocolName || '',
          transportType: x.transport_type || x.transportType || '',
          description: (x.description || '').trim(),
          dateCreated: x.date_created || x.dateCreated || null,
          userCreated: x.user_created || x.userCreated || '',
          dateChanged: x.date_changed || x.dateChanged || null,
          userChanged: x.user_changed || x.userChanged || ''
        }))

        this.page = 1
      } catch (err) {
        this.listError =
          (err && err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'Unknown error'
      } finally {
        this.listLoading = false
      }
    },
    async saveProtocol () {
      const ok = this.$refs.protocolForm.validate()
      if (!ok) return

      this.dialog.loading = true

      try {
        const apiBase = process.env.VUE_APP_API
        const apiVersion = process.env.VUE_APP_API_VERSION

        const payload = {
          protocol_code: this.form.protocolCode,
          protocol_name: this.form.protocolName,
          transport_type: this.form.transportType,
          description: this.form.description,
          is_active: this.form.isActive
        }

        let endpoint = ''
        let method = 'post'

        if (this.dialog.mode === 'create') {
          endpoint = `${apiBase}/api/${apiVersion}/register_protocol`
        } else {
          endpoint = `${apiBase}/api/${apiVersion}/update_protocol`
          payload.protocol_skey = this.form.protocolSkey
        }

        const res = await this.axios({
          method,
          url: endpoint,
          data: payload,
          validateStatus: () => true
        })

        if (!res.data || !res.data.isSuccess) {
          throw new Error(res.data?.message || 'Save failed')
        }

        await this.$swal({
          type: 'success',
          title: this.dialog.mode === 'create'
            ? 'Protocol created'
            : 'Protocol updated'
        })

        this.dialog.open = false
        await this.fetchItems()
      } catch (e) {
        await this.$swal({
          type: 'error',
          title: e.message || 'Save failed'
        })
      } finally {
        this.dialog.loading = false
      }
    },
    openCreateProtocol () {
      this.dialog.mode = 'create'
      this.dialog.open = true
      this.form = {
        protocolSkey: null,
        protocolCode: '',
        protocolName: '',
        transportType: '',
        description: '',
        isActive: 1
      }
      this.$nextTick(() => {
        this.$refs.protocolForm && this.$refs.protocolForm.resetValidation()
      })
    },
    openEditProtocol (item) {
      this.dialog.mode = 'edit'
      this.dialog.open = true

      this.form = {
        protocolSkey: item.protocolSkey,
        protocolCode: item.protocolCode,
        protocolName: item.protocolName,
        transportType: item.transportType,
        description: item.description,
        isActive: item.isActive ? 1 : 0
      }

      this.$nextTick(() => {
        this.$refs.protocolForm && this.$refs.protocolForm.resetValidation()
      })
    },
    async confirmDeleteProtocol (item) {
      const protocolSkey = item.protocolSkey || item.protocolSkey
      if (!protocolSkey) {
        await this.$swal({ type: 'error', title: this.$t('Error'), text: 'protocol_skey is missing' })
        return
      }

      // 1) confirm
      const result = await this.$swal({
        text: this.$t('ProtocolManagement.confirmDeleteProtocol'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: this.$t('delete'),
        cancelButtonText: this.$t('cancel')
      })
      const confirmed = result === true || (result && result.value)
      if (!confirmed) return

      // 2) loading
      this.$swal({
        title: this.$t('Processing...'),
        text: this.$t('Please wait'),
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: () => this.$swal.showLoading()
      })

      try {
        const endpoint =
          `${process.env.VUE_APP_API}/api/${process.env.VUE_APP_API_VERSION}` +
          `/delete_protocol/${encodeURIComponent(protocolSkey)}`

        const res = await this.axios.delete(endpoint)

        if (!res?.data?.isSuccess) {
          throw new Error(res?.data?.message || 'Delete failed')
        }

        // 3) ลบแถวออกจาก UI ทันที (ไม่ต้อง fetch ใหม่)
        const idx = this.items.findIndex(i => (i.protocolSkey || i.protocol_skey) === protocolSkey)
        if (idx !== -1) this.items.splice(idx, 1)

        await this.$swal({
          type: 'success',
          title: this.$t('Delete Success'),
          text: this.$t('ProtocolManagement.deleteSuccess')
        })
      } catch (e) {
        await this.$swal({
          type: 'error',
          title: this.$t('Delete failed'),
          text: e?.message || String(e)
        })
      }
    },
  }
}
</script>
<style>
.dialog-card {
  position: relative;
  border-radius: 12px;
}

.dialog-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  min-width: 36px !important;
  border-radius: 50%;
}
.dialog-actions {
  padding: 16px 24px;
}
</style>